import { useCallback, useMemo } from 'react'

import { ActionsProvider } from '../../providers/ActionsProvider/ActionsProvider'
import { TooltipProvider } from '../../providers/TooltipProvider/TooltipProvider'
import { SeatMap } from '../SeatMap'
import {
  getVenueSeatScale,
  isNmsuStyleSeatmapVenue,
} from '../../utilities/frontendSeatmapSizing'
import {
  buildSeatKey,
  getHeatColor,
  getMetricValue,
  HEATMAP_METRICS,
} from './salesVelocityHeatmapUtils'

const NO_OP = () => {}

export default function SalesVelocityHeatmapMap({
  mapping,
  background,
  seatBorderRadius,
  venue,
  bySeatKey,
  metricId,
  metricRanges,
}) {
  const metricConfig = HEATMAP_METRICS.find((m) => m.id === metricId)
  const range = metricRanges?.[metricId] || { min: 0, max: 0 }
  const venueSlug = venue?.slug
  const venueName = venue?.name
  const useFrontendSeatSizing = isNmsuStyleSeatmapVenue(venueSlug)
  const seatScale = getVenueSeatScale({ venueName, venueSlug })

  const seatKeyBySeatId = useMemo(() => {
    const map = new Map()
    if (!mapping?.seats) return map

    for (const seat of Object.values(mapping.seats)) {
      if (!seat?.seatId) continue
      const key = buildSeatKey(
        seat.sectionNumber,
        seat.rowNumber,
        seat.seatNumber
      )
      if (key) map.set(String(seat.seatId), key)
    }

    for (const section of Object.values(mapping.sections || {})) {
      if (section.zoomable !== false) continue
      for (const spot of section.spots || []) {
        const sid = String(spot)
        if (map.has(sid)) continue
        const seat = mapping.seats?.[sid]
        const key = buildSeatKey(
          seat?.sectionNumber ?? section.sectionNumber,
          seat?.rowNumber,
          seat?.seatNumber
        )
        if (key) map.set(sid, key)
      }
    }

    return map
  }, [mapping])

  const sectionMetrics = useMemo(() => {
    const bySection = new Map()
    Object.values(bySeatKey || {}).forEach((row) => {
      const section = String(row.section || '')
      if (!section) return
      const bucket = bySection.get(section) || { sum: 0, count: 0 }
      const value = getMetricValue(row, metricId)
      if (value != null) {
        bucket.sum += value
        bucket.count += 1
      }
      bySection.set(section, bucket)
    })
    const averages = new Map()
    bySection.forEach((bucket, section) => {
      if (bucket.count > 0) {
        averages.set(section, bucket.sum / bucket.count)
      }
    })
    return averages
  }, [bySeatKey, metricId])

  const getSeatState = useCallback(
    (seatId) => {
      const seatKey = seatKeyBySeatId.get(String(seatId))
      const analytics = seatKey ? bySeatKey?.[seatKey] : null
      const value = getMetricValue(analytics, metricId)
      const color = getHeatColor(
        value,
        range.min,
        range.max,
        metricConfig?.higherIsBetter !== false
      )

      return {
        disabled: true,
        color,
        analytics,
        metricValue: value,
      }
    },
    [
      seatKeyBySeatId,
      bySeatKey,
      metricId,
      range.min,
      range.max,
      metricConfig?.higherIsBetter,
    ]
  )

  const getSectionState = useCallback(
    (section) => {
      const sectionNumber = String(
        section?.sectionNumber ?? section?.sectionId ?? ''
      )
      const avg = sectionMetrics.get(sectionNumber)
      const color = getHeatColor(
        avg,
        range.min,
        range.max,
        metricConfig?.higherIsBetter !== false
      )
      return { disabled: true, color: avg != null ? color : '#d1d5db' }
    },
    [sectionMetrics, range.min, range.max, metricConfig?.higherIsBetter]
  )

  const getSeatTooltipDetails = useCallback(
    (seatId) => {
      const seat = mapping?.seats?.[seatId]
      const seatKey = seatKeyBySeatId.get(String(seatId))
      const analytics = seatKey ? bySeatKey?.[seatKey] : null

      const sectionNumber = analytics?.section ?? seat?.sectionNumber
      const rowNumber = analytics?.row ?? seat?.rowNumber
      const seatNumber = analytics?.seat ?? seat?.seatNumber

      if (sectionNumber == null && rowNumber == null && seatNumber == null) {
        return null
      }

      return { sectionNumber, rowNumber, seatNumber }
    },
    [mapping?.seats, seatKeyBySeatId, bySeatKey]
  )

  const legendItems = useMemo(
    () => [
      { label: 'Low', className: 'sales-velocity-legend-low' },
      { label: 'Medium', className: 'sales-velocity-legend-mid' },
      { label: 'High', className: 'sales-velocity-legend-high' },
      { label: 'No data', className: 'sales-velocity-legend-none' },
    ],
    []
  )

  if (!mapping || !background?.url) {
    return (
      <p className="text-muted mb-0">
        Seatmap background is not available for this venue.
      </p>
    )
  }

  return (
    <div className="sales-velocity-map-shell">
      <TooltipProvider>
        <ActionsProvider canEdit={false} legendItems={legendItems}>
          <SeatMap
            data={mapping}
            background={background}
            isMapDisabled={true}
            useFrontendSeatSizing={useFrontendSeatSizing}
            seatScale={seatScale}
            seatBorderRadius={seatBorderRadius}
            selectRegularSeats={NO_OP}
            handleSectionSelect={NO_OP}
            getSectionState={getSectionState}
            getSeatState={getSeatState}
            getSeatTooltipDetails={getSeatTooltipDetails}
            displayInventoryInfo={false}
            deriveInventoryInfoFrom={[]}
          />
        </ActionsProvider>
      </TooltipProvider>
    </div>
  )
}
